import type { SaveDataV1, SaveInventoryItemV1, SaveBuildingV1, SaveMonsterV1 } from './save-schema.js';
import type { SlimeState } from '../slime/slime-state.js';
import type { Inventory } from '../inventory/inventory.js';
import type { MutationSystem } from '../slime/mutation-system.js';
import type { SettlementGrid } from '../settlement/settlement-grid.js';
import type { BossEncounter } from '../combat/boss-encounter.js';

export interface GameSerializeInput {
  slime: SlimeState;
  position: { x: number; y: number; z: number };
  inventory: Inventory;
  mutations: MutationSystem;
  settlement?: SettlementGrid;
  boss?: BossEncounter;
}

export class SaveEngine {
  public static readonly CURRENT_VERSION = 1;

  public static serialize(input: GameSerializeInput): SaveDataV1 {
    const invItems: SaveInventoryItemV1[] = [];
    const allSlots = input.inventory.getSlots();
    for (let i = 0; i < allSlots.length; i++) {
      const slot = allSlots[i];
      if (slot) {
        invItems.push({
          slotIndex: i,
          itemId: slot.itemId,
          name: slot.name,
          quantity: slot.quantity,
          unitMassKg: slot.unitMassKg
        });
      }
    }

    const buildings: SaveBuildingV1[] = [];
    if (input.settlement) {
      for (const s of input.settlement.getAllStructures()) {
        buildings.push({
          blueprintId: s.type,
          gridX: s.gridX,
          gridZ: s.gridZ,
          createdAt: s.createdAt
        });
      }
    }

    const monsters: SaveMonsterV1[] = [];

    return {
      version: 1,
      timestamp: Date.now(),
      player: {
        massKg: input.slime.massKg,
        gelEnergy: input.slime.gelEnergy,
        position: { ...input.position }
      },
      inventory: {
        items: invItems
      },
      mutations: {
        unlockedTraitIds: input.mutations.getUnlockedTraits().map((t) => t.id),
        absorbedCounts: { ...input.mutations.getMaterialCounts() }
      },
      settlement: {
        buildings,
        monsters
      },
      dungeon: {
        bossDefeated: input.boss ? input.boss.isDefeated : false,
        bossCurrentHp: input.boss ? input.boss.currentHp : 0
      }
    };
  }

  public static deserialize(raw: unknown): SaveDataV1 {
    if (!raw || typeof raw !== 'object') {
      throw new Error('Corrupted save data: payload is not an object.');
    }

    const data = raw as Partial<SaveDataV1>;
    if (!data.version) {
      throw new Error('Invalid save data: missing version identifier.');
    }

    if (data.version === 1) {
      return {
        version: 1,
        timestamp: data.timestamp ?? Date.now(),
        player: {
          massKg: data.player?.massKg ?? 10.0,
          gelEnergy: data.player?.gelEnergy ?? 100,
          position: data.player?.position ?? { x: 0, y: 0.9, z: 0 }
        },
        inventory: {
          items: data.inventory?.items ?? []
        },
        mutations: {
          unlockedTraitIds: data.mutations?.unlockedTraitIds ?? [],
          absorbedCounts: data.mutations?.absorbedCounts ?? {}
        },
        settlement: {
          buildings: data.settlement?.buildings ?? [],
          monsters: data.settlement?.monsters ?? []
        },
        dungeon: {
          bossDefeated: data.dungeon?.bossDefeated ?? false,
          bossCurrentHp: data.dungeon?.bossCurrentHp ?? 250
        }
      };
    }

    throw new Error(`Unsupported save version: ${data.version}`);
  }

  public static exportToJsonString(input: GameSerializeInput): string {
    const data = SaveEngine.serialize(input);
    return JSON.stringify(data, null, 2);
  }

  public static importFromJsonString(jsonStr: string): SaveDataV1 {
    const parsed = JSON.parse(jsonStr);
    return SaveEngine.deserialize(parsed);
  }
}
